import { useGiftForm } from '../../../../components/GiftFormContext';
import { useTokenSearch } from '../../../../hooks/useTokenSearch';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import TextInput from '../../../../components/Inputs/Text';
import SearchIcon from '../../../../assets/icons/search.svg?react';
import { hapticsImpactLight } from '../../../../common/haptic';
import TokenList from '../../../../components/TokenList';
import {
  EVENT_GIFT_FLOW_TOKEN_SELECTED,
  sendEvent,
} from '../../../../common/analytics';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useEffect } from 'react';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
`;

const CategoryContainer = styled.div`
  padding: 12px 16px;
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  color: ${({ theme }) => theme.text.regular};
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.primary};
  font-size: 14px;
  cursor: pointer;
  width: 100%;
  font-weight: 600;
  width: fit-content;
  white-space: nowrap;
  ${({ selected, theme }) =>
    selected &&
    `
    border: 1px solid transparent;
    color: ${theme.primary.primary};
    background-color: ${theme.primary.background};
  `};
  @media ${PHONE_MEDIA_QUERY} {
    padding: 8px 8px;
    font-size: 12px;
  }
`;

const CategoryContainerInner = styled.div`
  display: flex;
  padding-top: 5px;
  gap: 16px;
  width: fit-content;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const CATEGORIES = ['ALL', 'POPULAR', 'STOCKS', 'PRESTOCKS', 'COMMODITIES'];

const CATEGORY_I18N_KEYS = {
  ALL: 'home.categoryAll',
  POPULAR: 'home.categoryPopular',
  STOCKS: 'home.categoryStocks',
  PRESTOCKS: 'home.categoryPrestocks',
  COMMODITIES: 'home.categoryCommodities',
};

const CATEGORY_ICON = {
  ALL: <img src='/burguer.webp' width={16} height={16} />,
  POPULAR: '🚀',
  STOCKS: '📈',
  PRESTOCKS: '🔒',
  COMMODITIES: '🌍',
};

const LPD_PRIORITY_TOKENS = {
  netflix: {
    firstOne: 'XsEH7wWfJJu2ZT3UCFeVfALnVA6CP5ur7Ee11KmzVpL',
    sortByCategory: 'STOCKS',
  },
  gold: {
    firstOne: 'Xsv9hRk1z5ystj9MhnA7Lq4vjSsLwzL2nxrwmwtD3re',
    sortByCategory: 'STOCKS',
  },
  mcdonalds: {
    firstOne: 'XsqE9cRRpzxcGKDXj1BJ7Xmg4GRhZoyY1KpmGSxAWT2',
    sortByCategory: 'STOCKS',
  },
  microsoft: {
    firstOne: 'XspzcW1PRtgf6Wj92HCiZdjzKCyFekVD8P5Ueh3dRMX',
    sortByCategory: 'STOCKS',
  },
  cocacola: {
    firstOne: 'XsaBXg8dU5cPM6ehmVctMkVqoiRG2ZjMo1cyBJ3AykQ',
    sortByCategory: 'STOCKS',
  },
  apple: {
    firstOne: 'XsbEhLAtcf6HdfpFZ5xEMdqW8nfAvcsP5bdudRLJzJp',
    sortByCategory: 'STOCKS',
  },
  openai: {
    firstOne: 'PreweJYECqtQwBtpxHL171nL2K6umo692gTm7Q3rpgF',
    sortByCategory: 'PRESTOCKS',
  },
  stocks: {
    firstOne: 'XsDoVfqeBukxuZHWhdvWHBhgEHjGNst4MLodqsJHzoB',
    sortByCategory: 'STOCKS',
  },
  checkfreegift: {
    firstOne: '3NZ9JMVBmGAqocybic2c7LQCJScmgsAZ6vQqTDzcqmJh',
    sortByCategory: 'POPULAR',
  },
};

const TokenSelector = ({ onContinue, isModal = false }) => {
  const { t } = useTranslation();
  const { setValue, showFreeGift, showBonus } = useGiftForm();
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');

  const {
    searchTerm,
    selectedCategory,
    displayTokens,
    isLoading,
    isFetching,
    hasMore,
    setSearchTerm,
    setSelectedCategory,
    setSortByCategory,
    loadMore,
  } = useTokenSearch(null, null);

  // Set sortByCategory state based on conditions
  useEffect(() => {
    if (lpd && LPD_PRIORITY_TOKENS[lpd]) {
      setSortByCategory(LPD_PRIORITY_TOKENS[lpd].sortByCategory);
    } else if (!lpd && !showFreeGift && showBonus) {
      setSortByCategory('PRESTOCKS');
    } else {
      setSortByCategory(null);
    }
  }, [lpd, showFreeGift, showBonus, setSortByCategory]);

  const sortedTokens = useMemo(() => {
    // Only need client-side sorting for LPD flow to prioritize firstOne token
    // Backend handles category sorting via sortByCategory parameter
    if (lpd && LPD_PRIORITY_TOKENS[lpd]) {
      const { firstOne } = LPD_PRIORITY_TOKENS[lpd];

      return [...displayTokens].sort((a, b) => {
        // Prioritize the specific token (firstOne)
        const aIsFirst = a.address === firstOne;
        const bIsFirst = b.address === firstOne;
        if (aIsFirst && !bIsFirst) return -1;
        if (!aIsFirst && bIsFirst) return 1;
        return 0;
      });
    }

    // Backend already sorted by category, no client-side sorting needed
    return displayTokens;
  }, [displayTokens, lpd]);

  const badges = useMemo(() => {
    const badgeConfigs = [];

    if (lpd) {
      const suggestedToken = LPD_PRIORITY_TOKENS[lpd]?.firstOne;
      if (suggestedToken) {
        badgeConfigs.push({
          text: 'Trending',
          shouldShow: (token) => token.address === suggestedToken,
        });
      }
    } else if (!lpd && !showFreeGift && showBonus) {
      badgeConfigs.push({
        text: '10% bonus',
        shouldShow: (token) =>
          token.categories?.some((cat) => cat.name === 'PRESTOCKS'),
      });
    }

    return badgeConfigs;
  }, [lpd, showBonus, showFreeGift]);

  const handleTokenSelect = (token) => {
    hapticsImpactLight();
    setValue('targetTokenAddress', token.address);
    setValue('network', token.network);
    sendEvent(EVENT_GIFT_FLOW_TOKEN_SELECTED, {
      token: token.address,
      network: token.network,
      is_free_gift: showFreeGift,
    });
    if (showBonus && token.name?.toLowerCase().includes('prestock')) {
      setValue('delivery', 'mail');
    } else {
      setValue('delivery', 'link');
    }
    // Auto-advance to next step when token is selected
    if (onContinue) {
      // Small delay to ensure form state is updated
      setTimeout(() => {
        onContinue();
      }, 100);
    }
  };

  // Always show the full token list - same UI for both modal and embedded mode
  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        {/* <label
          style={{ color: theme.text.title, fontSize: 14, fontWeight: 500 }}
        >
          Select an Asset to Gift
        </label> */}
        <TextInput
          placeholder={t('selectTokenModal.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          endIcon={<SearchIcon width={20} height={20} style={{ color: theme.text.regular }} />}
        />
      </div>
      <CategoriesContainer>
        <CategoryContainerInner>
          {CATEGORIES.map((category) => (
            <CategoryContainer
              key={category}
              onClick={() => {
                hapticsImpactLight();
                setSelectedCategory(category);
              }}
              selected={category === selectedCategory}
            >
              {CATEGORY_ICON[category]} {t(CATEGORY_I18N_KEYS[category])}
            </CategoryContainer>
          ))}
        </CategoryContainerInner>
      </CategoriesContainer>
      <TokenList
        tokens={sortedTokens}
        isLoading={isLoading}
        isFetching={isFetching}
        hasMore={hasMore}
        badges={badges}
        loadMore={loadMore}
        onTokenSelect={handleTokenSelect}
        showBalancesUSD={false}
        groupByCategory={false}
      />
    </Container>
  );
};

export default TokenSelector;
